import { useCallback, useMemo } from "react";

import { useIntl } from "react-intl";
import { useRecoilValue, useResetRecoilState } from "recoil";

import { type Discoverability, type MyLensesLensCta, type Visibility } from "../../gql-generated/types";
import {
    type LensPreviewMediaItem,
    discoverabilityById,
    isGameUserProvidedById,
    lensCreatorPayoutEnrolledById,
    lensCtaAtomById,
    lensDescriptionAtomById,
    lensPreviewMediaItemsAtomById,
    lensSpectaclesTagsAtomById,
    lensSurfaceTypeAtomById,
    lensTagsAtomById,
    lensVisibilityAtomById,
    releaseNoteAtomById,
} from "../../shared/state";

import { areSubmissionDetailsValid } from "./areSubmissionDetailsValid";

export const useLensDetails = (
    lensOrSubmissionId: string,
    previewValidationMessage?: string | null,
): {
    resetDetails: () => void;
    visibility: Visibility;
    tagsList: readonly string[];
    lensCta: MyLensesLensCta | null;
    lensPreviewMediaItems: LensPreviewMediaItem[];
    isGameUserProvided: boolean;
    isDetailsValid: boolean;
    detailsValidationError: string | undefined;
    discoverability: Discoverability | null;
    lensCreatorPayoutEnrolled: boolean;
    description: string;
    releaseNote: string | null;
    spectaclesTagsList: readonly string[];
} => {
    const visibility = useRecoilValue(lensVisibilityAtomById(lensOrSubmissionId));
    const tagsList = useRecoilValue(lensTagsAtomById(lensOrSubmissionId));
    const lensCta = useRecoilValue(lensCtaAtomById(lensOrSubmissionId));
    const lensPreviewMediaItems = useRecoilValue(lensPreviewMediaItemsAtomById(lensOrSubmissionId));
    const isGameUserProvided = useRecoilValue(isGameUserProvidedById(lensOrSubmissionId));
    const discoverability = useRecoilValue(discoverabilityById(lensOrSubmissionId));
    const lensCreatorPayoutEnrolled = useRecoilValue(lensCreatorPayoutEnrolledById(lensOrSubmissionId));
    const description = useRecoilValue(lensDescriptionAtomById(lensOrSubmissionId));
    const releaseNote = useRecoilValue(releaseNoteAtomById(lensOrSubmissionId));
    const spectaclesTagsList = useRecoilValue(lensSpectaclesTagsAtomById(lensOrSubmissionId));
    const surfaceType = useRecoilValue(lensSurfaceTypeAtomById(lensOrSubmissionId));

    const resetTagsList = useResetRecoilState(lensTagsAtomById(lensOrSubmissionId));
    const resetSpectaclesTagsList = useResetRecoilState(lensSpectaclesTagsAtomById(lensOrSubmissionId));
    const resetVisibility = useResetRecoilState(lensVisibilityAtomById(lensOrSubmissionId));
    const resetLensCta = useResetRecoilState(lensCtaAtomById(lensOrSubmissionId));
    const resetLensPreviewMediaItems = useResetRecoilState(lensPreviewMediaItemsAtomById(lensOrSubmissionId));
    const resetDiscoverability = useResetRecoilState(discoverabilityById(lensOrSubmissionId));
    const resetIsGameUserProvided = useResetRecoilState(isGameUserProvidedById(lensOrSubmissionId));
    const resetLensCreatorPayoutEnrolled = useResetRecoilState(lensCreatorPayoutEnrolledById(lensOrSubmissionId));
    const resetLensDescription = useResetRecoilState(lensDescriptionAtomById(lensOrSubmissionId));
    const resetReleaseNote = useResetRecoilState(releaseNoteAtomById(lensOrSubmissionId));

    const resetDetails = useCallback(() => {
        resetTagsList();
        resetSpectaclesTagsList();
        resetVisibility();
        resetLensCta();
        resetLensPreviewMediaItems();
        resetDiscoverability();
        resetIsGameUserProvided();
        resetLensCreatorPayoutEnrolled();
        resetLensDescription();
        resetReleaseNote();
    }, [
        resetTagsList,
        resetSpectaclesTagsList,
        resetVisibility,
        resetLensCta,
        resetLensPreviewMediaItems,
        resetDiscoverability,
        resetIsGameUserProvided,
        resetLensCreatorPayoutEnrolled,
        resetLensDescription,
        resetReleaseNote,
    ]);

    const intl = useIntl();

    const { isValid: isDetailsValid, errorMessage } = useMemo(
        () =>
            areSubmissionDetailsValid(surfaceType, lensPreviewMediaItems, isGameUserProvided, previewValidationMessage),
        [surfaceType, lensPreviewMediaItems, isGameUserProvided, previewValidationMessage],
    );

    const detailsValidationError = errorMessage ? intl.formatMessage(errorMessage) : undefined;

    return {
        resetDetails,
        visibility,
        tagsList,
        lensCta,
        lensPreviewMediaItems,
        isGameUserProvided,
        isDetailsValid,
        detailsValidationError,
        discoverability,
        lensCreatorPayoutEnrolled,
        description,
        releaseNote,
        spectaclesTagsList,
    };
};
